import React, { useMemo } from 'react';

import { Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import { matchSorter } from 'match-sorter';
import { Spacer } from 'src/components';
import { CRMConnectedIcon } from 'src/components/icons/CRMConnectedIcon';
import { CrmVaultAction } from 'src/components/send-for-signature/components/AddIntegration';
import type { Apollo } from 'src/gql/generated';
import {
  CRM_CONNECT_ADMIN_ONLY_MESSAGE,
  getIsApiDeckConnectionCallable,
  useUserIntegrations,
} from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  card: {
    '&:hover': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    width: '100%',
  },
  cardDisabled: {
    '&:hover': {
      border: `1px solid ${theme.appColors.rule}`,
    },
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  checkMark: {
    bottom: -4,
    height: 16,
    left: 16,
    position: 'absolute',
    width: 16,
  },
  empty: {
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
  icon: {
    display: 'block',
    height: 26,
    width: 26,
  },
  imageContainer: {
    display: 'inline-block',
    position: 'relative',
  },
  menuItem: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    overflow: 'auto',
  },
}));

export function AddedApiDeckConnections({
  className,
  searchText,
}: {
  className?: string;
  searchText?: string;
}) {
  const { classes } = useStyles();
  const { allConnections, loading } = useUserIntegrations();
  const searchedConnections = useMemo(() => {
    const keys: Array<keyof Apollo.ApiDeckConnection> = ['name'];
    if (!searchText) return allConnections;

    return matchSorter(allConnections, searchText, { keys });
  }, [allConnections, searchText]);

  if (loading)
    return (
      <div className={clsx(classes.root, className)}>
        {Array.from({ length: 3 }).map((_, i) => {
          return (
            <div key={i} className={classes.card}>
              <div className={classes.header}>
                <Skeleton height={26} variant="rectangular" width={26} />
                <Skeleton height={24} variant="text" width={48} />
                <Spacer />
              </div>
              <Typography>
                <Skeleton height={24} variant="text" width="100%" />
              </Typography>
              <Typography>
                <Skeleton height={24} variant="text" width="100%" />
              </Typography>
            </div>
          );
        })}
      </div>
    );

  if (!searchedConnections.length)
    return (
      <div className={classes.empty}>
        <Typography align="center" variant="subtitle1">
          No results match your search.
        </Typography>
      </div>
    );

  return (
    <div className={classes.root}>
      {searchedConnections.map((connection, index) => {
        return <ApiDeckConnection key={index} className={classes.card} connection={connection} />;
      })}
    </div>
  );
}

export function ApiDeckConnection({
  className,
  connection,
}: {
  className: string;
  connection: Pick<
    Apollo.ApiDeckConnection,
    'icon' | 'integration' | 'name' | 'state' | 'tagLine' | '__typename'
  >;
}) {
  const { classes } = useStyles();
  const isConnectionCallable = getIsApiDeckConnectionCallable(connection);

  // The card shows the gate itself (dimmed, not-allowed cursor, native title), so no tooltip.
  return (
    <CrmVaultAction integration={connection.integration}>
      <CrmVaultAction.Control tooltip={false}>
        {({ disabled, open }) => (
          <div
            aria-disabled={disabled}
            className={clsx(className, disabled && classes.cardDisabled)}
            title={disabled ? CRM_CONNECT_ADMIN_ONLY_MESSAGE : undefined}
            onClick={open}
          >
            <div className={classes.header}>
              <div className={classes.imageContainer}>
                <img className={classes.icon} src={connection.icon} />
                {isConnectionCallable && <CRMConnectedIcon className={classes.checkMark} />}
              </div>
              <div>
                <Typography variant="body2">{connection.name}</Typography>
                {isConnectionCallable && (
                  <Typography color="secondary" variant="caption">
                    Connected
                  </Typography>
                )}
              </div>
            </div>
            <Typography>{connection.tagLine}</Typography>
          </div>
        )}
      </CrmVaultAction.Control>
    </CrmVaultAction>
  );
}
